import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Tooltip } from 'radix-ui'
import { cn } from '@/lib/cn'

/**
 * Button — the one button in the app (spec §3.13.2).
 *
 * Rules (spec §3.3.1): ONE `primary` per view. Everything else is `secondary`,
 * `outline`, `ghost` or `danger`. No hover lift and no glow: hover is a surface/colour
 * change only, press is a 1.5% scale (transform, so it never reflows a toolbar).
 */
export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap',
    'rounded-md font-medium leading-none',
    'transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-standard',
    'hover:[transform:translateY(var(--lift))] active:[transform:scale(var(--press))]',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    '[&_svg]:pointer-events-none',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground shadow-primary-btn hover:bg-primary-hover hover:shadow-lift',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
        outline: 'border border-border bg-card text-foreground shadow-control hover:border-input/60 hover:bg-surface-hover',
        ghost: 'text-content-secondary hover:bg-surface-hover hover:text-foreground',
        danger: 'bg-destructive text-destructive-foreground shadow-control hover:bg-destructive/90',
        link: 'h-auto px-0 text-link underline-offset-2 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-[13px] [&_svg]:size-3.5',
        md: 'h-9 px-3.5 text-sm [&_svg]:size-4',
        lg: 'h-10 px-4 text-sm [&_svg]:size-4',
        icon: 'size-9 [&_svg]:size-4',
        'icon-sm': 'size-8 [&_svg]:size-3.5',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Leading icon. Replaced by a spinner while `loading`, so the label never shifts. */
  icon?: ReactNode
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, icon, loading, children, disabled, type = 'button', title, ...rest },
  ref,
) {
  // Icon-only buttons get a real tooltip (hover AND keyboard focus) from their accessible name,
  // so nobody has to guess what an icon does (E-UX). Text buttons keep the plain title.
  const hasText = typeof children === 'string' || typeof children === 'number'
    || (Array.isArray(children) && children.some((c) => typeof c === 'string' || typeof c === 'number'))
  const iconOnly = (size === 'icon' || size === 'icon-sm') && !hasText
  const tip = iconOnly ? (title ?? (rest['aria-label'] as string | undefined)) : undefined
  const button = (
    <button
      ref={ref}
      type={type}
      title={tip ? undefined : title}
      {...rest}
      aria-label={(rest['aria-label'] as string | undefined) ?? (iconOnly ? title : undefined)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
    >
      {loading ? (
        <span
          aria-hidden
          className="size-3.5 animate-spin rounded-full border-[1.5px] border-current border-r-transparent"
        />
      ) : (
        icon
      )}
      {children}
    </button>
  )
  if (!tip) return button
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content sideOffset={6} className="z-[100] max-w-xs rounded-md bg-tooltip px-2.5 py-1.5 text-xs font-medium text-tooltip-foreground shadow-e3 data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0">
          {tip}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  )
})
